import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { StorageValues } from 'src/app/modules/shared/entities/storage-values.enum';
import { ApiService } from 'core/api/api.service';
import { LoadingState } from 'core/common-entities/loading-state.enum';
import { ExportUtilsService } from 'core/export-utils/export-utils.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { LnOrderResponse } from '../../entities/ln-order-entity';
import { LnOrderHeader } from '../../entities/ln-order-header-entity';
import { LnOrderSortColumn, SortDirection } from '../../quotes/entities/sort.enum';
import { AdvancedSearchRequestVars, AdvancedSearchService } from '../../services/advanced-search.service';
import { LnOrdersFilters } from '../entities/ln-orders-filters';
import { LnOrdersTemplateValues } from '../entities/ln-orders-template-values';
import { LnOrdersPayload } from '../entities/ln-orders-payload';

@Injectable({
  providedIn: 'root'
})
export class LnOrdersService {
  private urls = {
    ordersList: '/api/ln-order/list',
    orderPrint: '/api/ln-order/printOrder/',
    templateValues: '/api/ln-order/templatevalues',
    lnOrderHeader: '/api/ln-order/header/',
    trackingNumberList: '/api/ln-order/trackingnumbers/',
    lnOrderDetails: '/api/ln-order/items',
  };
  private defaultSort = {
    direction: 'desc',
    sortBy: 'orderDate'
  };

  private ordersResultCount = {
    exact: 0,
    partial: 0
  };

  private listPayload: LnOrdersFilters = {
    date: {
      startDate: null,
      endDate: null
    },
    nameFilter: {
      employeeId: '',
      bpNumber: '',
      customerName: '',
      orderNumber: '',
      quoteNumber: '',
      statusCode: '',
      totalAmount: '',
      poNumber: '',
      displayedSearchValue: '',
      partialSearch: true
    },
    sort: {
      direction: SortDirection[this.defaultSort.direction],
      sortBy: LnOrderSortColumn[this.defaultSort.sortBy]
    },
    page: 1
  };

  private orderObject = {
    url: this.urls.ordersList,
    payload: this.listPayload
  };

  private pendingRequests = new Subscription();
  pageSize = 15;
  isProcessing$ = new Subject<LoadingState>();
  templateValues = {
    OrderSortType: null,
    SortDirection: null,
    LnOrderStatus: null
  };
  ordersList$ = new Subject<any>();
  ordersCount$ = new Subject<any>();
  customerId: string;
  templateValuesResult: LnOrdersTemplateValues;
  dataLoaded = {
    templateValues: false,
    orders: false,
  };
  private appStrings: AppLocalization;

  constructor(
    private apiService: ApiService,
    private exportUtilsService: ExportUtilsService,
    private advancedSearchService: AdvancedSearchService,
    private appInfoService: AppInfoService,
  ) {
    this.appStrings = this.appInfoService.appStrings;
  }

  /**
   * Get order list and sets payload
   * @param lnOrdersPayload - LN order info
   * @param sort - Sort state
   * @param shouldCache - Should cache flag
   */
  getOrderList(lnOrdersPayload?: LnOrdersPayload, sort?: Sort, shouldCache?: boolean) {
    if (lnOrdersPayload) {
      if (shouldCache) {
        sessionStorage.setItem(StorageValues.CachedFilters + StorageValues.LnOrderKey,
          JSON.stringify(lnOrdersPayload));
      }

      this.listPayload.date = {
        startDate: lnOrdersPayload.startDate,
        endDate: lnOrdersPayload.endDate
      };

      this.listPayload.nameFilter = {
        employeeId: lnOrdersPayload.accountManager ? lnOrdersPayload.accountManager.employeeId : null,
        bpNumber: lnOrdersPayload.bpNumber,
        customerName: lnOrdersPayload.customerName,
        orderNumber: lnOrdersPayload.orderNumber,
        quoteNumber: lnOrdersPayload.quoteNumber,
        statusCode: lnOrdersPayload.lnOrderStatus?.toString(),
        totalAmount: lnOrdersPayload.dollarAmount?.toString(),
        poNumber: lnOrdersPayload.poNumber,
        displayedSearchValue: lnOrdersPayload.displayedSearchValue,
        partialSearch: true
      };
      this.resetSort();
    }

    if (sort) {
      this.listPayload.sort = {
        direction: SortDirection[sort.direction],
        sortBy: LnOrderSortColumn[sort.active]
      };
      sessionStorage.setItem(StorageValues.CachedSort + StorageValues.LnOrderKey, JSON.stringify(sort));
    }
    this.updatePage(1);
  }

  /**
   * Sets the default sort
   */
  resetSort() {
    this.orderObject.payload.sort = {
      direction: SortDirection[this.defaultSort.direction],
      sortBy: LnOrderSortColumn[this.defaultSort.sortBy]
    };
  }

  /**
   * Print LN order
   * @param orderNumber - LN order number
   */
  printOrder(orderNumber: string) {
    const printWindow = this.exportUtilsService.openPrintFile(this.appStrings.loading_LnOrderIsProcessing);
    this.apiService.getBlob(this.urls.orderPrint + orderNumber)
      .subscribe(data => this.exportUtilsService.printFile(data, printWindow));
  }

  /**
   * Updates page
   * @param pageIndex  - Page index
   */
  updatePage(pageIndex: number) {
    this.isProcessing$.next(LoadingState.loading);
    const requestVars: AdvancedSearchRequestVars = {
      recordCount$: this.ordersCount$,
      recordList$: this.ordersList$,
      resultCount: this.ordersResultCount
    };
    this.orderObject.payload.page = pageIndex;
    this.getResultsForRequest(this.orderObject, requestVars);
  }

  /**
   * Gets the records for the selected page
   * @param requestObj - Record list and count
   * @param requestVars - Object containing url and payload
   */
  private getResultsForRequest(requestObj: any, requestVars: AdvancedSearchRequestVars) {
    // cancel existing requests (if any), and reinitialize the subscription
    this.pendingRequests.unsubscribe();
    this.pendingRequests = new Subscription();

    const offsets = this.advancedSearchService.getOffsetsForPage(requestObj.payload, requestVars, this.pageSize);

    const requestObs = this.advancedSearchService.getRequestObservables<LnOrderResponse>(LnOrderResponse, requestObj, offsets);

    const exactCall = requestObs.exact.subscribe((exactResultObj: LnOrderResponse) => {
      const parsedExactObj = exactResultObj;
      requestVars.recordList$.next(parsedExactObj.lnOrders);
      requestVars.recordCount$.next(parsedExactObj.count);
      this.isProcessing$.next(LoadingState.partiallyLoaded);
      // only update the count on the first page of results. This prevents total page issues when the object is generated.
      if (offsets.exactOffset === 0) {
        this.ordersResultCount.exact = exactResultObj.count;
      }
      const partialCall = requestObs.partial.subscribe((partialResultObj: LnOrderResponse) => {
        const parsedPartialObj = partialResultObj;
        requestVars.recordList$.next(parsedExactObj.lnOrders.concat(parsedPartialObj.lnOrders).slice(0, this.pageSize));
        // only update the count on the first page of results. This prevents total page issues when the object is generated.
        if (offsets.partialOffset === 0) {
          this.ordersResultCount.partial = partialResultObj.count;
        }
        requestVars.recordCount$.next(this.ordersResultCount.exact + this.ordersResultCount.partial);
        this.isProcessing$.next(LoadingState.fullyLoaded);
      });
      this.pendingRequests.add(partialCall); // store call to be able to cancel it later
    });
    this.pendingRequests.add(exactCall); // store call to be able to cancel it later
  }

  /**
   * Gets template values info
   * @param callback - Template values callback
   */
  getTemplateValues(callback: (...args: any[]) => any) {
    const cachedData = this.templateValues.OrderSortType &&
      this.templateValues.SortDirection && this.templateValues.LnOrderStatus;
    const observableList = [];

    if (cachedData) {
      observableList.push(
        of({
          LnOrderSortType: this.templateValues.OrderSortType,
          SortDirection: this.templateValues.SortDirection,
          LnOrderStatus: this.templateValues.LnOrderStatus,
        })
      );
    } else {
      observableList.push(this.apiService.get(this.urls.templateValues));
    }

    forkJoin(observableList).subscribe(resultArray => {
      const results: any = resultArray[0];
      this.templateValues.OrderSortType = results.LnOrderSortType;
      this.templateValues.SortDirection = results.SortDirection;
      this.templateValues.LnOrderStatus = results.LnOrderStatus;

      callback(this.templateValues);
    });
  }

  /**
   * Gets order header info
   * @param lnOrderNumber - LN order number
   * @param callback - Order header callback
   */
  getOrderHeader(lnOrderNumber: string, callback: (...args: any[]) => any) {
    this.apiService.get<LnOrderHeader>(this.urls.lnOrderHeader + lnOrderNumber).subscribe(result => callback(result));
  }

  /**
   * Gets tracking number list
   * @param orderNumber - LN order number
   * @returns - List of tracking numbers for selected order
   */
  getOrderTrackingNumberList(orderNumber: string) {
    return this.apiService.get(`${this.urls.trackingNumberList}${orderNumber}`);
  }

  /**
   * Gets order details info
   * @param lnOrderNumber - LN order number
   * @param callback - Order details callback
   */
  getOrderDetails(params: any, callback: (...args: any[]) => any) {
    this.apiService.post(this.urls.lnOrderDetails, params).subscribe(result => callback(result));
  }

}
